import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Search, Eye } from "lucide-react";
import { supplierOrderAPI, supplierAPI } from "@/lib/api";

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

const STATUS_LABELS = { brouillon: "Brouillon", envoyee: "Envoyée", confirmee: "Confirmée", recue: "Recue", annulee: "Annulée" };

export default function SupplierOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => {
    supplierOrderAPI.list().then(r => setOrders(r.data.items || r.data)).catch(() => {});
    supplierAPI.list({ limit: 500 }).then(r => setSuppliers(r.data.items || r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const getSupplierName = (sid) => {
    const s = suppliers.find(sup => sup.id === sid);
    return s ? s.company : "-";
  };

  const filtered = search
    ? orders.filter(o => o.number?.toLowerCase().includes(search.toLowerCase()) || getSupplierName(o.supplier_id).toLowerCase().includes(search.toLowerCase()))
    : orders;

  return (
    <div className="page-container" data-testid="supplier-orders-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Commandes fournisseurs
        </h1>
        <p className="text-slate-500 mt-1">Toutes les commandes passees aupres de vos fournisseurs</p>
      </div>
      <Card className="p-6 border border-slate-200 rounded-lg bg-white">
        <div className="relative w-full sm:w-72 mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Rechercher numéro, fournisseur..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="supplier-order-search" />
        </div>
        <div className="responsive-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="uppercase text-xs text-slate-500">Numéro</TableHead>
                <TableHead className="uppercase text-xs text-slate-500">Fournisseur</TableHead>
                <TableHead className="uppercase text-xs text-slate-500">Date</TableHead>
                <TableHead className="uppercase text-xs text-slate-500">Statut</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 text-right">Total TTC</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(o => (
                <TableRow key={o.id} className="table-row-hover">
                  <TableCell className="font-medium">{o.number}</TableCell>
                  <TableCell>{getSupplierName(o.supplier_id)}</TableCell>
                  <TableCell>{o.date ? new Date(o.date).toLocaleDateString("fr-FR") : "-"}</TableCell>
                  <TableCell><span className={`status-badge status-${o.status}`}>{STATUS_LABELS[o.status] || o.status}</span></TableCell>
                  <TableCell className="money-value">{formatEur(o.total_ttc)}</TableCell>
                  <TableCell>
                    {o.supplier_id && (
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => navigate(`/fournisseurs/${o.supplier_id}`)} data-testid={`view-supplier-order-${o.id}`}>
                        <Eye size={12} className="mr-1" />Voir
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Aucune commande fournisseur</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
